import { forwardRef, useState } from "react";
import { Sparkles } from "lucide-react";

import { MedicinePicker } from "./MedicinePicker";
import { PrescriptionLines } from "./PrescriptionLines";
import type { PickedMedicine } from "./MedicinePicker";
import { Button } from "@/components/ui/button";
import type { EncounterPrescriptionItem } from "@/shared/types/encounter";
import type { PrescriptionTemplateResponse } from "@/shared/types/prescriptionTemplate";

type KdTab = "thuoc" | "vattu" | "dichvu" | "dinhduong";

const KD_TABS: { key: KdTab; label: string; isNew?: boolean }[] = [
  { key: "thuoc", label: "Thuốc" },
  { key: "vattu", label: "Vật tư" },
  { key: "dichvu", label: "Dịch vụ" },
  { key: "dinhduong", label: "Dinh dưỡng", isNew: true },
];

const DIET_CHOICES = ["Ít đường", "Ít muối", "Giàu chất xơ", "Đủ nước", "Giàu đạm", "Ít béo"];

/**
 * Kê đơn — the four tabs and everything under them (TD-7b/1 slice 3, #18806).
 *
 * <p><b>The nutrition tab is why this takes `onAppendPlan` and not a SOAP patcher.</b> Choosing a
 * diet appends a line to the encounter's plan, which is clinical-note state the workspace owns.
 * Handing this panel the patcher would let the prescription section write anywhere in the note —
 * the §9 domain leak, and the same shape as the follow-up date staying with the page.
 *
 * <p>Owns only the selected tab and the diet chips. The `rx` list stays with the workspace because
 * signing reads it; `error` and `qtyError` are displayed here but decided there, since it is the
 * signing flow that refuses.
 *
 * <p>Forwards a ref: signing scrolls to this section when a quantity is missing, so it has to be
 * addressable.
 */
export const PrescriptionSection = forwardRef<
  HTMLDivElement,
  {
    items: EncounterPrescriptionItem[];
    onPatch: (index: number, patch: Partial<EncounterPrescriptionItem>) => void;
    onPatchImmediate: (index: number, patch: Partial<EncounterPrescriptionItem>) => void;
    onRemove: (index: number) => void;
    onPickMedicine: (m: PickedMedicine) => void;
    onApplyTemplate: (t: PrescriptionTemplateResponse) => void;
    onOpenTemplateModal: () => void;
    /** Appends one line to the encounter's plan — the panel never touches the note itself. */
    onAppendPlan: (line: string) => void;
    /** The workspace's shared error line. */
    error: string | null;
    /** The drug name signing refused on, or null. */
    qtyError: string | null;
    /** Symptom chips, shown as context on the nutrition tab. */
    chiefChips: string[];
    readOnly: boolean;
    onError: (message: string | null) => void;
    /** #19184 — medicineId → clash description, marked on the card. */
    allergyMarks?: ReadonlyMap<string, string>;
  }
>(function PrescriptionSection(
  {
    items,
    onPatch,
    onPatchImmediate,
    onRemove,
    onPickMedicine,
    onApplyTemplate,
    onOpenTemplateModal,
    onAppendPlan,
    error,
    qtyError,
    chiefChips,
    readOnly,
    onError,
    allergyMarks,
  },
  ref
) {
  const [kdTab, setKdTab] = useState<KdTab>("thuoc");
  const [diet, setDiet] = useState<string[]>([]);

  return (
    <>
      <div ref={ref} />
      {error && (
        <p className="mb-2.5 text-[13px] text-destructive">{error}</p>
      )}
      {qtyError && (
        <p className="mb-2.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">
          Vui lòng nhập số lượng (&gt; 0) cho thuốc: <b>{qtyError}</b> trước khi hoàn tất khám.
        </p>
      )}
      <div className="mb-3 flex flex-wrap gap-1 border-b">
        {KD_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ${
              kdTab === t.key
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setKdTab(t.key)}
          >
            {t.label}
            {t.isNew && (
              <span className="rounded bg-primary px-1 py-0.5 text-[9px] font-semibold text-primary-foreground">
                MỚI
              </span>
            )}
          </button>
        ))}
      </div>

      {kdTab === "thuoc" && (
        <>
          {!readOnly && (
            <MedicinePicker
              onPickMedicine={onPickMedicine}
              onApplyTemplate={onApplyTemplate}
              onOpenTemplateModal={onOpenTemplateModal}
              onError={onError}
            />
          )}
          <PrescriptionLines
            items={items}
            onPatch={onPatch}
            onPatchImmediate={onPatchImmediate}
            onRemove={onRemove}
            qtyError={Boolean(qtyError)}
            readOnly={readOnly}
            allergyMarks={allergyMarks}
          />
        </>
      )}

      {kdTab === "vattu" && (
        <p className="text-sm text-muted-foreground">
          Vật tư tiêu hao được tính ở bước Hoá đơn.
        </p>
      )}
      {kdTab === "dichvu" && (
        <p className="text-sm text-muted-foreground">
          Chỉ định dịch vụ / CLS (X-quang, siêu âm…) ở mục “Chỉ định CLS /
          dịch vụ”. Thu phí ở bước Hoá đơn.
        </p>
      )}

      {kdTab === "dinhduong" && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              {chiefChips.length > 0 ? (
                <>
                  <b>Theo triệu chứng</b> ({chiefChips.join(", ")}): chọn chế
                  độ ăn phù hợp bên dưới.
                </>
              ) : (
                <>Chọn chế độ ăn phù hợp bên dưới.</>
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {DIET_CHOICES.map(
              (c) => {
                const on = diet.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    className={`rounded-md border px-2.5 py-1 text-sm transition-colors disabled:opacity-50 ${
                      on
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input bg-background text-foreground hover:bg-accent"
                    }`}
                    onClick={() =>
                      setDiet((p) =>
                        on ? p.filter((x) => x !== c) : [...p, c]
                      )
                    }
                    disabled={readOnly}
                  >
                    {c} {on ? "✓" : ""}
                  </button>
                );
              }
            )}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={readOnly || diet.length === 0}
            onClick={() => onAppendPlan(`Chế độ ăn: ${diet.join(", ")}.`)}
          >
            Thêm vào lời dặn
          </Button>
        </div>
      )}
    </>
  );
});
